import { PdfLayoutConfig } from '@common/types';
// eslint-disable-next-line import/no-unresolved
import { Content } from 'pdfmake/interfaces';

/**
 * Creates pdfmake layout functions from layout configuration data
 * Each configuration value is converted to a function that returns that value
 *
 * Example:
 * Input:  { hLineWidth: 2, vLineColor: "#000" }
 * Output: { hLineWidth: () => 2, vLineColor: () => "#000" }
 */
export function createLayoutFromConfig(config: PdfLayoutConfig): Record<string, () => any> {
  return Object.entries(config).reduce((layout, [key, value]) => {
    if (value !== undefined) {
      layout[key] = () => value;
    }
    return layout;
  }, {} as Record<string, () => any>);
}

export interface SerializedPdfResult {
  content: Record<string, any>;
  layoutConfigs: Record<string, PdfLayoutConfig>;
}

/**
 * Extracts layout configuration from a pdfmake layout object
 * Converts functions back to their returned values
 */
function extractLayoutConfig(layoutObj: object): PdfLayoutConfig | null {
  const config: PdfLayoutConfig = {};
  for (const [key, value] of Object.entries(layoutObj)) {
    if (typeof value === 'function') {
      config[key] = value();
    }
  }
  return Object.keys(config).length > 0 ? config : null;
}

function generateLayoutName(counter: number): string {
  return `layout_${counter}`;
}

/**
 * Serializes pdfmake Content and automatically extracts layout configurations
 * Returns both the serialized content and the extracted layout configs
 *
 * Naming convention: Sequential naming (layout_0, layout_1, etc.)
 * Example: First layout found → "layout_0", Second layout → "layout_1"
 */
export function serializePdfContent(content: Content): SerializedPdfResult {
  const extractedLayouts = new Map<string, PdfLayoutConfig>();
  let layoutCounter = 0;

  const serializedContent = JSON.parse(
    JSON.stringify(content, (key, value) => {
      // Handle layout objects - extract config and replace with reference
      if (key === 'layout' && typeof value === 'object' && value !== null) {
        const layoutConfig = extractLayoutConfig(value);
        if (layoutConfig && Object.keys(layoutConfig).length > 0) {
          const layoutName = generateLayoutName((layoutCounter += 1));
          extractedLayouts.set(layoutName, layoutConfig);
          return { configuredLayout: layoutName };
        }
      }

      // Convert functions to null - they can't be serialized safely
      if (typeof value === 'function') {
        return null;
      }

      return value;
    }),
  );

  return {
    content: serializedContent,
    layoutConfigs: Object.fromEntries(extractedLayouts),
  };
}

/**
 * Deserializes pdfmake Content from database storage
 * Converts layout references back to actual pdfmake layout functions
 *
 * Example: { configuredLayout: "noLines" } → { hLineWidth: () => 0, vLineWidth: () => 0 }
 */
export function deserializePdfContent(
  serializedContent: Record<string, any>,
  layoutConfigs?: Record<string, PdfLayoutConfig>,
): Content {
  return JSON.parse(JSON.stringify(serializedContent), (_key, value) => {
    // Replace layout references with actual layout functions
    if (value && typeof value === 'object' && value.configuredLayout && layoutConfigs) {
      const layoutName = value.configuredLayout as string;
      const layoutConfig = layoutConfigs[layoutName];
      if (layoutConfig) {
        return createLayoutFromConfig(layoutConfig);
      }
    }
    return value;
  });
}
